import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  UserGroupIcon,
  CheckIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import {
  fetchPortalUserMappings,
  updatePortalUserMapping,
  fetchPsaResources,
} from '../api/client';
import { useAuth } from '../hooks/useAuth';

function psaSystemLabel(sys) {
  const s = `${sys || ''}`.toLowerCase();
  if (s === 'autotask') return 'AutoTask';
  if (s === 'connectwise') return 'ConnectWise';
  return s || '—';
}

// Maps portal users (login-built directory) to a PSA resource/member per MSP, so a
// PSA time entry logged at ticket-close time can be attributed to the right person.
export default function PortalUserPsaMappingManager() {
  const { user } = useAuth();
  const isGlobalAdmin = !!user?.isGlobalAdmin;
  const adminMspCodes = useMemo(
    () => (user?.adminMspCodes || []).map((c) => `${c}`.toUpperCase()),
    [user]
  );
  const canEdit = useCallback(
    (mspCode) => isGlobalAdmin || adminMspCodes.includes(`${mspCode || ''}`.toUpperCase()),
    [isGlobalAdmin, adminMspCodes]
  );

  const [users, setUsers] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [msps, setMsps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedMsp, setSelectedMsp] = useState('');
  // PSA resources fetched on demand, keyed by MSP code: { system, resources, loading, error }.
  const [resByMsp, setResByMsp] = useState({});
  // Per-row edit state for the selected MSP, keyed by upn: { resourceId, resourceName, resourceEmail }.
  const [localMap, setLocalMap] = useState({});
  const [savingUpn, setSavingUpn] = useState(null);
  const [rowError, setRowError] = useState({});

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPortalUserMappings();
      const u = Array.isArray(data?.users) ? data.users : [];
      const m = Array.isArray(data?.mappings) ? data.mappings : [];
      const s = Array.isArray(data?.msps) ? data.msps : [];
      setUsers(u);
      setMappings(m);
      setMsps(s);
      setSelectedMsp((prev) => prev || (s[0]?.mspCode || ''));
    } catch (err) {
      setError(err.message || 'Failed to load portal user mappings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Existing mappings indexed by `${mspCode}::${upn}` for fast lookup.
  const mappingIndex = useMemo(() => {
    const idx = {};
    mappings.forEach((m) => {
      const key = `${`${m.mspCode}`.toUpperCase()}::${`${m.upn}`.toLowerCase()}`;
      idx[key] = m;
    });
    return idx;
  }, [mappings]);

  const selectedMspObj = useMemo(
    () => msps.find((m) => `${m.mspCode}`.toUpperCase() === `${selectedMsp}`.toUpperCase()) || null,
    [msps, selectedMsp]
  );

  // When the MSP changes, seed the per-row edit state from the stored mappings.
  useEffect(() => {
    if (!selectedMsp) { setLocalMap({}); return; }
    const seed = {};
    users.forEach((u) => {
      const key = `${`${selectedMsp}`.toUpperCase()}::${`${u.upn}`.toLowerCase()}`;
      const m = mappingIndex[key];
      if (m && m.psaResourceId) {
        seed[`${u.upn}`.toLowerCase()] = {
          resourceId: `${m.psaResourceId}`,
          resourceName: `${m.psaResourceName || ''}`,
          resourceEmail: `${m.psaResourceEmail || ''}`,
        };
      }
    });
    setLocalMap(seed);
    setRowError({});
  }, [selectedMsp, users, mappingIndex]);

  const resState = resByMsp[`${selectedMsp}`.toUpperCase()] || null;
  const resources = resState?.resources || [];

  const fetchResources = async () => {
    if (!selectedMsp) return;
    const code = `${selectedMsp}`.toUpperCase();
    setResByMsp((prev) => ({ ...prev, [code]: { ...(prev[code] || {}), loading: true, error: null } }));
    try {
      const data = await fetchPsaResources(selectedMsp);
      setResByMsp((prev) => ({
        ...prev,
        [code]: { system: data?.system || '', resources: Array.isArray(data?.resources) ? data.resources : [], loading: false, error: null },
      }));
    } catch (err) {
      setResByMsp((prev) => ({ ...prev, [code]: { ...(prev[code] || {}), loading: false, error: err.message || 'Failed to fetch PSA users' } }));
    }
  };

  const setRowResource = (upn, resourceId) => {
    const key = `${upn}`.toLowerCase();
    if (!resourceId) {
      setLocalMap((prev) => { const next = { ...prev }; delete next[key]; return next; });
      return;
    }
    const r = resources.find((x) => `${x.id}` === `${resourceId}`);
    setLocalMap((prev) => ({
      ...prev,
      [key]: { resourceId: `${resourceId}`, resourceName: r?.name || '', resourceEmail: r?.email || '' },
    }));
  };

  const handleSave = async (u) => {
    const key = `${u.upn}`.toLowerCase();
    const edit = localMap[key];
    setRowError((prev) => ({ ...prev, [key]: null }));
    setSavingUpn(key);
    try {
      const payload = {
        upn: u.upn,
        psaResourceId: edit?.resourceId || '',
        psaResourceName: edit?.resourceName || '',
        psaResourceEmail: edit?.resourceEmail || '',
        psaSystem: resState?.system || selectedMspObj?.ticketingSystem || '',
      };
      const saved = await updatePortalUserMapping(selectedMsp, payload);
      // Reconcile the local mappings list so dirty-state clears.
      setMappings((prev) => {
        const idxKey = `${`${selectedMsp}`.toUpperCase()}::${key}`;
        const without = prev.filter(
          (m) => `${`${m.mspCode}`.toUpperCase()}::${`${m.upn}`.toLowerCase()}` !== idxKey
        );
        if (saved?.mapped) {
          without.push({
            mspCode: selectedMsp,
            upn: key,
            psaSystem: payload.psaSystem,
            psaResourceId: payload.psaResourceId,
            psaResourceName: payload.psaResourceName,
            psaResourceEmail: payload.psaResourceEmail,
          });
        }
        return without;
      });
    } catch (err) {
      setRowError((prev) => ({ ...prev, [key]: err.message || 'Failed to save' }));
    } finally {
      setSavingUpn(null);
    }
  };

  // Whether a row's current selection differs from the stored mapping.
  const isDirty = useCallback((u) => {
    const key = `${u.upn}`.toLowerCase();
    const idxKey = `${`${selectedMsp}`.toUpperCase()}::${key}`;
    const stored = mappingIndex[idxKey];
    const storedId = stored?.psaResourceId ? `${stored.psaResourceId}` : '';
    const editId = localMap[key]?.resourceId || '';
    return storedId !== editId;
  }, [localMap, mappingIndex, selectedMsp]);

  const editable = canEdit(selectedMsp);
  const credsConfigured = !!selectedMspObj?.credentialsConfigured;
  const mappedCount = useMemo(() => {
    const code = `${selectedMsp}`.toUpperCase();
    return mappings.filter((m) => `${m.mspCode}`.toUpperCase() === code && m.psaResourceId).length;
  }, [mappings, selectedMsp]);

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <UserGroupIcon className="h-7 w-7 text-blue-400" />
          <h1 className="text-2xl font-bold text-gray-100">PSA User Mapping</h1>
        </div>
        <p className="text-sm text-gray-500 max-w-3xl">
          Match each portal user to a user in the MSP's ticketing system (AutoTask resource / ConnectWise member).
          When an analyst logs time while closing an alert, the PSA time entry is attributed to their mapped user.
          A user with no mapping can still close tickets — they just can't log time. The portal user list is built
          automatically as people sign in.
        </p>
      </div>

      {!isGlobalAdmin && adminMspCodes.length === 0 && (
        <div className="card mb-6 flex items-center gap-3 text-sm text-gray-400">
          <LockClosedIcon className="h-4 w-4 text-gray-500" />
          <span>Read-only view — admin access is required to manage PSA user mappings.</span>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={load} className="ml-auto underline hover:no-underline text-red-400">Retry</button>
        </div>
      )}

      {/* MSP selector + fetch */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">MSP</label>
          <select
            value={selectedMsp}
            onChange={(e) => setSelectedMsp(e.target.value)}
            disabled={loading || msps.length === 0}
            className="input-field min-w-[240px] text-sm"
          >
            {msps.length === 0 && <option value="">No MSPs available</option>}
            {msps.map((m) => (
              <option key={m.mspCode} value={m.mspCode}>
                {m.displayName ? `(${m.mspCode}) ${m.displayName}` : m.mspCode}
                {` — ${psaSystemLabel(m.ticketingSystem)}`}
              </option>
            ))}
          </select>
        </div>
        {selectedMsp && (
          <button
            onClick={fetchResources}
            disabled={!credsConfigured || resState?.loading}
            title={credsConfigured ? 'Load PSA users for this MSP' : 'Save PSA credentials for this MSP first (PSA Configuration page)'}
            className="btn-secondary px-3 py-2 text-sm inline-flex items-center gap-1.5 disabled:opacity-40"
          >
            {resState?.loading ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <ArrowPathIcon className="h-4 w-4" />}
            Fetch PSA users
          </button>
        )}
        <div className="ml-auto text-xs text-gray-500 self-center">
          {users.length} portal user{users.length === 1 ? '' : 's'} · {mappedCount} mapped in {selectedMsp || '—'}
        </div>
      </div>

      {selectedMsp && !credsConfigured && (
        <div className="mb-4 p-3 bg-amber-900/20 border border-amber-700/40 rounded-lg text-amber-300 text-xs">
          No PSA credentials are saved for {selectedMsp}. Configure them on the PSA Configuration page before mapping users.
        </div>
      )}
      {resState?.error && (
        <div className="mb-4 p-3 bg-red-900/20 border border-red-700/40 rounded-lg text-red-300 text-xs">
          {resState.error}
        </div>
      )}
      {selectedMsp && credsConfigured && !resState && (
        <div className="mb-4 text-xs text-gray-500">
          Click <span className="text-gray-300">Fetch PSA users</span> to load the resource list, then assign each portal user.
        </div>
      )}

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="text-sm w-full">
            <thead>
              <tr className="border-b border-gray-800">
                {['Portal User', 'PSA User', ''].map((label) => (
                  <th key={label} className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {loading && (
                <tr>
                  <td colSpan={3} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <ArrowPathIcon className="h-8 w-8 text-blue-500 animate-spin" />
                      <span className="text-gray-500 text-sm">Loading portal users...</span>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && users.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-6 py-12 text-center">
                    <UserGroupIcon className="h-10 w-10 text-gray-700 mx-auto mb-3" />
                    <p className="text-gray-400 font-medium">No portal users yet</p>
                    <p className="text-gray-600 text-sm mt-1">Users appear here after they sign in to the portal.</p>
                  </td>
                </tr>
              )}

              {!loading && users.map((u) => {
                const key = `${u.upn}`.toLowerCase();
                const edit = localMap[key];
                const dirty = isDirty(u);
                const saving = savingUpn === key;
                const hasResources = resources.length > 0;
                // Build the dropdown options: sort ascending by name, then hoist any PSA
                // user whose email matches this portal user's UPN to the top (flagged as
                // the likely match). Finally, if the current mapping's resource isn't in
                // the fetched list (or none fetched yet), surface it so it's not lost.
                const upnLc = `${u.upn}`.toLowerCase();
                const sorted = [...resources].sort((a, b) =>
                  `${a.name || ''}`.localeCompare(`${b.name || ''}`, undefined, { sensitivity: 'base' })
                );
                const matchIdx = sorted.findIndex((o) => `${o.email || ''}`.toLowerCase() === upnLc);
                let likelyId = null;
                let ordered = sorted;
                if (matchIdx >= 0) {
                  likelyId = `${sorted[matchIdx].id}`;
                  ordered = [sorted[matchIdx], ...sorted.slice(0, matchIdx), ...sorted.slice(matchIdx + 1)];
                }
                const opts = [...ordered];
                if (edit?.resourceId && !opts.some((o) => `${o.id}` === `${edit.resourceId}`)) {
                  opts.unshift({ id: edit.resourceId, name: edit.resourceName || `(id ${edit.resourceId})`, email: edit.resourceEmail || '' });
                }
                return (
                  <tr key={u.upn} className="hover:bg-gray-800/50 transition-colors align-top">
                    <td className="px-4 py-3">
                      <div className="text-gray-200">{u.displayName || u.upn}</div>
                      <div className="text-xs text-gray-500 font-mono">{u.upn}</div>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={edit?.resourceId || ''}
                        disabled={!editable || (!hasResources && !edit?.resourceId)}
                        onChange={(ev) => setRowResource(u.upn, ev.target.value)}
                        className="input-field w-full max-w-sm text-xs disabled:opacity-60"
                      >
                        <option value="">— Not mapped —</option>
                        {opts.map((o) => (
                          <option key={o.id} value={o.id}>
                            {`${o.id}` === likelyId ? '★ likely match — ' : ''}{o.name}{o.email ? ` · ${o.email}` : ''}
                          </option>
                        ))}
                      </select>
                      {!hasResources && !edit?.resourceId && (
                        <p className="mt-1 text-[11px] text-gray-600">Fetch PSA users to assign.</p>
                      )}
                      {rowError[key] && <p className="mt-1 text-xs text-red-400">{rowError[key]}</p>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {editable && (
                        <button
                          onClick={() => handleSave(u)}
                          disabled={!dirty || saving}
                          className="btn-primary px-3 py-1.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
                        >
                          {saving ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <CheckIcon className="h-3.5 w-3.5" />}
                          Save
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
